import React, { useEffect, useState } from 'react';
import useKraExternalInputStore from '../../store/kraExternalInputStore';
import useAuthStore from '../../store/authStore';
import useUIStore from '../../store/uiStore';
import { getDepartments } from '../../services/employeeService';
import ScrollX from '../../components/ScrollX';
import KRAFilters, { matchesName } from '../../components/KRAFilters';

const ScoreSelector = ({ value, onChange }) => (
  <div className="flex gap-1">
    {[0, 1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        className={`w-7 h-7 rounded text-xs font-bold border ${
          value === n ? 'bg-komal-blue text-white border-komal-blue' : 'bg-white text-gray-500 border-gray-300 hover:border-komal-blue'
        }`}
      >
        {n}
      </button>
    ))}
  </div>
);

const QueueRow = ({ row, onSubmit, isSubmitting, showDepartment }) => {
  const [metricNote, setMetricNote] = useState('');
  const [score, setScore] = useState(null);
  const [comment, setComment] = useState('');
  const [showResponses, setShowResponses] = useState(false);

  return (
    <>
      <tr className="border-b border-gray-50 last:border-0 align-top hover:bg-gray-50">
        <td className="px-5 py-3">
          <div className="font-medium text-gray-900">{row.employee_name}</div>
          {showDepartment && <div className="text-xs text-gray-400 mt-0.5">{row.external_department}</div>}
          {row.month && (
            <div className="text-xs text-gray-400 mt-0.5">
              {new Date(row.month).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}
            </div>
          )}
          {/* MULTI-SCORER: Show progress of existing responses */}
          {row.existing_responses?.length > 0 && (
            <div className="text-xs text-gray-500 mt-1">
              {row.responses_received || 0}/{row.existing_responses.length} responses
              {row.average_score !== null && <span className="ml-1 font-semibold text-gray-700">Avg: {row.average_score.toFixed(1)}</span>}
            </div>
          )}
        </td>
        <td className="px-5 py-3">
          <div className="font-medium text-gray-900">{row.kra_name}</div>
          <div className="text-xs text-gray-500">{row.kpi}</div>
        </td>
        <td className="px-5 py-3">
          <input
            type="text"
            placeholder="Factual value (e.g. rejection %, delay days)"
            value={metricNote}
            onChange={(e) => setMetricNote(e.target.value)}
            className="w-full text-xs border border-gray-300 rounded px-2 py-1"
          />
          <div className="text-xs text-gray-400 mt-1">{row.evidence_source}</div>
        </td>
        <td className="px-5 py-3">
          <ScoreSelector value={score} onChange={setScore} />
          <input
            type="text"
            placeholder="Comment (required if score disagrees with the metric)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="mt-2 w-full text-xs border border-gray-200 rounded px-2 py-1"
          />
        </td>
        <td className="px-5 py-3 space-y-1">
          <button
            disabled={isSubmitting || score === null}
            onClick={() => onSubmit(row, { score, factual_metric: { note: metricNote }, comment })}
            className="w-full px-3 py-1.5 text-xs rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-40"
          >
            Submit
          </button>
          {/* MULTI-SCORER: Show button to view other responses if any exist */}
          {row.existing_responses?.length > 0 && (
            <button
              type="button"
              onClick={() => setShowResponses(!showResponses)}
              className="w-full px-3 py-1.5 text-xs rounded bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold"
            >
              {showResponses ? 'Hide' : `View ${row.existing_responses.length}`}
            </button>
          )}
        </td>
      </tr>
      {/* MULTI-SCORER: Show existing responses from other scorers */}
      {showResponses && row.existing_responses?.length > 0 && (
        <tr className="bg-blue-50 border-b border-gray-50">
          <td colSpan={5} className="px-5 py-3">
            <div className="text-xs space-y-2">
              <div className="font-semibold text-gray-700">Responses from {row.external_department}:</div>
              <div className="space-y-1">
                {row.existing_responses.map((resp, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-gray-600 bg-white px-2 py-1 rounded">
                    <span className="font-medium">{resp.scorer_name}</span>
                    <span className="text-gray-400">·</span>
                    <span className="font-bold text-blue-700">{resp.score}/5</span>
                    {resp.comment && <span className="text-gray-500">"{resp.comment}"</span>}
                    <span className="text-gray-400 ml-auto">{resp.submitted_at ? new Date(resp.submitted_at).toLocaleDateString() : 'pending'}</span>
                  </div>
                ))}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const CrossInputsPage = () => {
  const { user } = useAuthStore();
  const { queue, isLoading, isSubmitting, fetchQueue, submitInput } = useKraExternalInputStore();
  const showToast = useUIStore((s) => s.showToast);
  const isPrivileged = user?.role === 'CEO' || user?.role === 'HR';
  const [departments, setDepartments] = useState([]);
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState(''); // "YYYY-MM", '' = every month

  // Local only: the queue is already scoped by department server-side and
  // is small, so name/month narrow what's loaded rather than re-querying.
  const visibleQueue = queue.filter(
    (row) => matchesName(row.employee_name, nameFilter) && (!monthFilter || (row.month || '').slice(0, 7) === monthFilter)
  );

  useEffect(() => {
    if (isPrivileged) {
      getDepartments().then((res) => setDepartments(res.data.departments || []));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrivileged]);

  useEffect(() => {
    // Privileged roles (CEO/HR): no department param = the backend returns
    // the full company-wide queue (see get_external_input_queue). Everyone
    // else always gets scoped to their own department server-side
    // regardless of what's passed, so this only actually changes behavior
    // for CEO/HR.
    fetchQueue(isPrivileged ? (departmentFilter || undefined) : user?.department_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.department_id, isPrivileged, departmentFilter]);

  const handleSubmit = async (row, data) => {
    // source_department_id = the department slot this queue row stands for. Without it the
    // server falls back to the submitter's own home department, which is wrong for a CEO/HR
    // user filling another department's slot (e.g. Quality's, for a Quality HOD) and fails with
    // 400 "your department isn't one of the required ones".
    const result = await submitInput(row.cycle_id, {
      kra_item_id: row.kra_item_id,
      source_department_id: row.external_department_id,
      ...data,
    });
    showToast(result.success ? 'Input submitted and locked' : result.error, result.success ? 'success' : 'error');
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
          <h1 className="text-2xl font-bold text-white">Cross-Department Input Queue</h1>
          {isPrivileged && (
            <select
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">All Departments</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          )}
        </div>
        <p className="text-sm text-gray-500 mb-6">
          {isPrivileged
            ? 'As HR/CEO you can see and submit or correct any department\'s pending input — filter above to narrow it down.'
            : 'Only KRAs assigned to your department show up here — nothing else about these employees is visible to you.'}
        </p>

        <KRAFilters
          name={nameFilter}
          onNameChange={setNameFilter}
          month={monthFilter}
          onMonthChange={setMonthFilter}
          shown={visibleQueue.length}
          total={isLoading ? null : queue.length}
        />

        {isLoading ? (
          <p className="text-gray-500">Loading...</p>
        ) : queue.length === 0 ? (
          <div className="glass-card p-6 text-sm text-gray-500">Nothing needs input right now.</div>
        ) : visibleQueue.length === 0 ? (
          <div className="glass-card p-6 text-sm text-gray-500">No pending input matches these filters.</div>
        ) : (
          <div className="glass-card overflow-hidden">
            <ScrollX>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-100">
                    <th className="px-5 py-2 font-medium">Employee</th>
                    <th className="px-5 py-2 font-medium">KRA</th>
                    <th className="px-5 py-2 font-medium">Objective metric</th>
                    <th className="px-5 py-2 font-medium">Score (0–5)</th>
                    <th className="px-5 py-2 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleQueue.map((row) => (
                    <QueueRow
                      key={`${row.cycle_id}-${row.kra_item_id}-${row.external_department_id}`}
                      row={row}
                      onSubmit={handleSubmit}
                      isSubmitting={isSubmitting}
                      showDepartment={isPrivileged && !departmentFilter}
                    />
                  ))}
                </tbody>
              </table>
            </ScrollX>
          </div>
        )}
      </div>
    </div>
  );
};

export default CrossInputsPage;
